import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AgentCallback,
  AgentState,
  ApplicationContact,
  ApplicationDraft,
  ApplicationDraftKind,
  ApplicationEvent,
} from "@resume-cloudflare/shared";
import { api, ApiError } from "./api";
import { useMutationToast } from "../hooks/useMutationToast";

export type ApplicationStatus =
  | "interested"
  | "preparing"
  | "approved"
  | "applied"
  | "interviewing"
  | "offer"
  | "rejected"
  | "withdrawn";

export const APPLICATION_STATUSES: ApplicationStatus[] = [
  "interested",
  "preparing",
  "approved",
  "applied",
  "interviewing",
  "offer",
  "rejected",
  "withdrawn",
];

export interface ApplicationRow {
  id: string;
  user_id: string;
  url: string;
  company_id: string | null;
  company_name: string | null;
  company_tier: number | null;
  role: string;
  status: ApplicationStatus;
  listing_active: boolean;
  created_at: number;
  updated_at: number;
  agent_state: AgentState | null;
  agent_version: number;
  last_alarm_at: number | null;
  // Populated when ?include_counts=1 is set on /api/applications. The kanban
  // cards rely on these so they don't N+1 the timeline endpoint per row.
  score: number | null;
  event_count: number;
  contact_count: number;
  draft_count: number;
}

export interface ApplicationsResponse {
  applications: ApplicationRow[];
}

export function useApplications(
  status?: ApplicationStatus,
  options?: { includeCounts?: boolean },
) {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (options?.includeCounts) params.set("include_counts", "1");
  const qs = params.toString() ? `?${params.toString()}` : "";
  return useQuery({
    queryKey: ["applications", status ?? "all", options?.includeCounts ? "counts" : "bare"],
    queryFn: () => api<ApplicationsResponse>(`/api/applications${qs}`),
    staleTime: 10_000,
  });
}

export interface CreateApplicationInput {
  url: string;
  status?: ApplicationStatus;
  role?: string;
  company_id?: string;
  // Session 5 PR3 — when a non-monitored URL is pasted with no role, the
  // server Browser-Renders the page and creates a synthetic ad_hoc_jobs row.
  // Pass false to opt out (legacy "missing_role" 400 surface).
  allow_ad_hoc?: boolean;
}

export interface CreateApplicationResult {
  application: ApplicationRow;
  already_existed: boolean;
  // True when the server fell through the ad-hoc extract+score path for this
  // POST. Lets the SPA show a "Extracted from page" affordance.
  ad_hoc?: boolean;
}

export function useCreateApplication() {
  const qc = useQueryClient();
  return useMutationToast<CreateApplicationResult, ApplicationsError, CreateApplicationInput>({
    mutationFn: async (input) => {
      try {
        return await api<CreateApplicationResult>("/api/applications", {
          method: "POST",
          body: JSON.stringify(input),
        });
      } catch (err) {
        throw toApiError(err);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["applications"] });
    },
    success: (data) => {
      if (data.already_existed) {
        return `Already tracked as ${data.application.status}.`;
      }
      if (data.ad_hoc) {
        return `Tracked as ${data.application.status}. Extracted from page.`;
      }
      return `Tracked as ${data.application.status}.`;
    },
    errorPrefix: "Track failed",
  });
}

// Drag-drop kanban needs an optimistic status mutation: the drop animation
// shouldn't wait on a round-trip, but we need to revert if the DO 4xx/5xxs.
// We use react-query's onMutate snapshot so any failure rolls every cached
// `applications` query back to its pre-drop state.
export function useUpdateApplicationStatus() {
  const qc = useQueryClient();
  return useMutationToast<
    { application: ApplicationRow },
    ApplicationsError,
    { id: string; status: ApplicationStatus; reason?: string },
    { snapshots: Array<[readonly unknown[], ApplicationsResponse | undefined]> }
  >({
    mutationFn: async ({ id, status, reason }) => {
      try {
        return await api<{ application: ApplicationRow }>(
          `/api/applications/${id}`,
          {
            method: "PATCH",
            body: JSON.stringify(reason ? { status, reason } : { status }),
          },
        );
      } catch (err) {
        throw toApiError(err);
      }
    },
    onMutate: async ({ id, status }) => {
      await qc.cancelQueries({ queryKey: ["applications"] });
      const queries = qc.getQueriesData<ApplicationsResponse>({
        queryKey: ["applications"],
      });
      for (const [key, data] of queries) {
        if (!data) continue;
        qc.setQueryData<ApplicationsResponse>(key, {
          applications: data.applications.map((a) =>
            a.id === id ? { ...a, status, updated_at: Date.now() } : a,
          ),
        });
      }
      return { snapshots: queries };
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx) return;
      for (const [key, data] of ctx.snapshots) {
        qc.setQueryData(key, data);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["applications"] });
    },
    success: false, // drag-drop fires too often for a toast on each move
    errorPrefix: "Status update failed",
  });
}

export function useDeleteApplication() {
  const qc = useQueryClient();
  return useMutationToast<{ ok: true }, ApplicationsError, string>({
    mutationFn: async (id) => {
      try {
        return await api<{ ok: true }>(`/api/applications/${id}`, {
          method: "DELETE",
        });
      } catch (err) {
        throw toApiError(err);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["applications"] });
    },
    success: "Application deleted.",
    errorPrefix: "Delete failed",
  });
}

// ── Session 5 PR2 — DO-backed sub-routes ─────────────────────────────────

export interface ApplicationTimeline {
  events: ApplicationEvent[];
  contacts: ApplicationContact[];
  drafts: ApplicationDraft[];
  agent_state: AgentState | null;
  last_alarm_at: number | null;
}

export function useApplicationTimeline(applicationId: string | null) {
  return useQuery({
    queryKey: ["application-timeline", applicationId],
    enabled: applicationId !== null,
    queryFn: () =>
      api<ApplicationTimeline>(`/api/applications/${applicationId}/timeline`),
    staleTime: 5_000,
  });
}

// Exactly what went out with a submission (worker migration 0052): the frozen
// resume + letter PDFs, the form screenshots, and the approved answers. Null
// when nothing was recorded (404).
export interface SubmissionRecordItem {
  id: string;
  kind: "resume_pdf" | "cover_letter_pdf" | "form" | "confirmation";
  seq: number;
  label: string | null;
  page_url: string | null;
  created_at: number;
  url: string;
}
export interface SubmissionRecord {
  submission: {
    submitted_at: number | null;
    mode: string | null;
    confirmation_url: string | null;
    fields: Array<{ label: string; value: string | null }>;
  } | null;
  items: SubmissionRecordItem[];
}

export function useSubmissionRecord(applicationId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ["submission-record", applicationId],
    enabled: applicationId !== null && enabled,
    queryFn: async () => {
      try {
        return await api<SubmissionRecord>(`/api/apply/${applicationId}/submission-record`);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    staleTime: 60_000,
  });
}

function invalidateAppDetail(qc: ReturnType<typeof useQueryClient>, id: string) {
  qc.invalidateQueries({ queryKey: ["application-timeline", id] });
  qc.invalidateQueries({ queryKey: ["applications"] });
}

export function useAddNote(applicationId: string) {
  const qc = useQueryClient();
  return useMutationToast<
    { event: ApplicationEvent },
    ApplicationsError,
    { text: string }
  >({
    mutationFn: async (input) => {
      try {
        return await api<{ event: ApplicationEvent }>(
          `/api/applications/${applicationId}/note`,
          { method: "POST", body: JSON.stringify(input) },
        );
      } catch (err) {
        throw toApiError(err);
      }
    },
    onSuccess: () => invalidateAppDetail(qc, applicationId),
    success: "Note added.",
    errorPrefix: "Add note failed",
  });
}

export function useAddContact(applicationId: string) {
  const qc = useQueryClient();
  return useMutationToast<
    { contact: ApplicationContact },
    ApplicationsError,
    {
      name: string;
      role?: string;
      email?: string;
      linkedin_url?: string;
      notes?: string;
    }
  >({
    mutationFn: async (input) => {
      try {
        return await api<{ contact: ApplicationContact }>(
          `/api/applications/${applicationId}/contact`,
          { method: "POST", body: JSON.stringify(input) },
        );
      } catch (err) {
        throw toApiError(err);
      }
    },
    onSuccess: () => invalidateAppDetail(qc, applicationId),
    success: "Contact added.",
    errorPrefix: "Add contact failed",
  });
}

export function useAddDraft(applicationId: string) {
  const qc = useQueryClient();
  return useMutationToast<
    { draft: ApplicationDraft },
    ApplicationsError,
    { kind: ApplicationDraftKind; ref_id?: string; preview?: string }
  >({
    mutationFn: async (input) => {
      try {
        return await api<{ draft: ApplicationDraft }>(
          `/api/applications/${applicationId}/draft`,
          { method: "POST", body: JSON.stringify(input) },
        );
      } catch (err) {
        throw toApiError(err);
      }
    },
    onSuccess: () => invalidateAppDetail(qc, applicationId),
    success: "Draft attached.",
    errorPrefix: "Attach draft failed",
  });
}

export function useAddInterview(applicationId: string) {
  const qc = useQueryClient();
  return useMutationToast<
    { event: ApplicationEvent; scheduled: AgentState["scheduled"] },
    ApplicationsError,
    { label: string; scheduled_at: number; notes?: string }
  >({
    mutationFn: async (input) => {
      try {
        return await api<{
          event: ApplicationEvent;
          scheduled: AgentState["scheduled"];
        }>(`/api/applications/${applicationId}/interview`, {
          method: "POST",
          body: JSON.stringify(input),
        });
      } catch (err) {
        throw toApiError(err);
      }
    },
    onSuccess: () => invalidateAppDetail(qc, applicationId),
    success: "Interview scheduled.",
    errorPrefix: "Add interview failed",
  });
}

export function useScheduleAction(applicationId: string) {
  const qc = useQueryClient();
  return useMutationToast<
    { agent_state: AgentState },
    ApplicationsError,
    { label: string; callback: AgentCallback; fire_at: number }
  >({
    mutationFn: async (input) => {
      try {
        return await api<{ agent_state: AgentState }>(
          `/api/applications/${applicationId}/schedule`,
          { method: "POST", body: JSON.stringify(input) },
        );
      } catch (err) {
        throw toApiError(err);
      }
    },
    onSuccess: () => invalidateAppDetail(qc, applicationId),
    success: "Action scheduled.",
    errorPrefix: "Schedule failed",
  });
}

export function useCancelScheduled(applicationId: string) {
  const qc = useQueryClient();
  return useMutation<
    { agent_state: AgentState },
    ApplicationsError,
    { scheduledId: string }
  >({
    mutationFn: async ({ scheduledId }) => {
      try {
        return await api<{ agent_state: AgentState }>(
          `/api/applications/${applicationId}/schedule/${scheduledId}`,
          { method: "DELETE" },
        );
      } catch (err) {
        throw toApiError(err);
      }
    },
    onSuccess: () => invalidateAppDetail(qc, applicationId),
  });
}

export interface ApplicationsError {
  status: number;
  error: string;
}

function toApiError(err: unknown): ApplicationsError {
  if (err instanceof ApiError) {
    let payload: { error?: string } = {};
    try {
      payload = JSON.parse(err.message);
    } catch {
      // not JSON
    }
    return {
      status: err.status,
      error: payload.error ?? err.message ?? "request_failed",
    };
  }
  return { status: 0, error: "network_error" };
}
